// JQ4-45 · US-AUDIT-005 — useCursorPagination
//
// A generic, reusable cursor-based "load more" hook built on TanStack Query's
// useInfiniteQuery. Designed to be the FIRST server-side keyset pagination
// primitive in apps/admin and to be reusable by future stories (JQ4-49, etc.).
//
// Contract
// ────────
// Caller supplies:
//   - queryKey   : unknown[]          — must include all filter values so that
//                                        changing filters resets to page 1
//   - fetchPage  : (cursor: string | null) => Promise<{ items, nextCursor, hasMore }>
//   - enabled?   : boolean            — pass false to disable the query (e.g. no org selected)
//
// Returns:
//   - items              : T[]              — all loaded items (flattened across pages)
//   - hasMore            : boolean          — whether a next page is available
//   - isLoading          : boolean          — first-load spinner
//   - isFetchingNextPage : boolean          — "load more" in progress
//   - fetchNextPage      : () => void       — call on "Load more" button
//   - error              : Error | null
//   - refetch            : () => void
//
// Spec refs: R-PAGINATION-01, R-PAGINATION-02, R-LIST-04, R-LIST-05, R-LIST-06

import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
  hasMore: boolean;
}

interface UseCursorPaginationOptions<T> {
  queryKey: unknown[];
  fetchPage: (cursor: string | null) => Promise<CursorPage<T>>;
  /** Set to false to prevent the query from running (e.g. waiting for org selection). */
  enabled?: boolean;
}

interface UseCursorPaginationResult<T> {
  items: T[];
  hasMore: boolean;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  error: Error | null;
  refetch: () => void;
}

export function useCursorPagination<T>({
  queryKey,
  fetchPage,
  enabled = true,
}: UseCursorPaginationOptions<T>): UseCursorPaginationResult<T> {
  const { data, hasNextPage, isLoading, isFetchingNextPage, fetchNextPage, error, refetch } =
    useInfiniteQuery<CursorPage<T>, Error>({
      queryKey,
      queryFn: ({ pageParam }) => fetchPage((pageParam as string | null | undefined) ?? null),
      // getNextPageParam: if hasMore and nextCursor is present, return the cursor for the next fetch.
      // Returning undefined signals "no more pages" to TanStack Query.
      getNextPageParam: (lastPage) =>
        lastPage.hasMore && lastPage.nextCursor != null ? lastPage.nextCursor : undefined,
      // initialPageParam is required by TanStack Query v5 for infinite queries.
      initialPageParam: null as string | null,
      enabled,
    });

  // Flatten all pages into a single item list (newest → oldest per server order)
  const items = useMemo<T[]>(() => data?.pages.flatMap((p) => p.items) ?? [], [data?.pages]);

  return {
    items,
    hasMore: hasNextPage ?? false,
    isLoading,
    isFetchingNextPage,
    fetchNextPage: () => void fetchNextPage(),
    error: error ?? null,
    refetch: () => void refetch(),
  };
}
